import { useParams } from 'react-router-dom';
import { usePortfolioHealth, useTurfAnalysis, useCannibalization } from '@/hooks/usePortfolio';
import { LoadingPage, LoadingCard } from '@/components/ui/LoadingSpinner';
import { ErrorPage } from '@/components/ui/ErrorMessage';
import { ExplainabilityCard, generatePortfolioExplanation } from '@/components/ui/ExplainabilityCard';
import type { CannibalizationAnalysis } from '@/api/client';

function getHealthLabel(score: number): { text: string; color: string } {
  if (score >= 0.7) return { text: 'Good', color: 'text-success-400' };
  if (score >= 0.4) return { text: 'Fair', color: 'text-warning-400' };
  return { text: 'Poor', color: 'text-danger-400' };
}

function getHHILabel(hhi: number): string {
  if (hhi < 0.15) return 'Highly Diversified';
  if (hhi < 0.25) return 'Balanced';
  if (hhi < 0.5) return 'Moderately Concentrated';
  return 'Highly Concentrated';
}

function getRiskColor(risk: string): string {
  switch (risk) {
    case 'low': return 'text-success-400';
    case 'medium': return 'text-warning-400';
    case 'high': return 'text-danger-400';
    default: return 'text-text-secondary';
  }
}

export function PortfolioPage() {
  const { clientId } = useParams();
  const healthQuery = usePortfolioHealth(clientId);
  const turfQuery = useTurfAnalysis(clientId);
  const cannibalizationQuery = useCannibalization(clientId);

  if (healthQuery.isLoading) {
    return <LoadingPage message="Loading portfolio data..." />;
  }

  if (healthQuery.error) {
    return <ErrorPage error={healthQuery.error} onRetry={() => healthQuery.refetch()} />;
  }

  const health = healthQuery.data;
  const turf = turfQuery.data;
  const cannibalization = cannibalizationQuery.data;

  const healthLabel = health ? getHealthLabel(health.health_score) : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-white mb-6">Portfolio Analysis</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <div className="metric-label">Portfolio Health</div>
          <div className={`metric-value ${healthLabel?.color || 'text-text-secondary'}`}>
            {healthLabel?.text || '-'}
          </div>
          <div className="text-xs text-text-secondary mt-1">
            HHI: {health?.hhi.toFixed(2) || '-'} ({health ? getHHILabel(health.hhi) : '-'})
          </div>
        </div>
        <div className="card p-4">
          <div className="metric-label">SKU Count</div>
          <div className="metric-value">{health?.sku_count || '-'}</div>
          <div className="text-xs text-text-secondary mt-1">
            {health?.at_risk_count || 0} at risk
          </div>
        </div>
        {cannibalizationQuery.isLoading ? (
          <LoadingCard />
        ) : (
          <div className="card p-4">
            <div className="metric-label">Cannibalization Risk</div>
            <div className={`metric-value capitalize ${getRiskColor(cannibalization?.overall_risk || 'low')}`}>
              {cannibalization?.overall_risk || '-'}
            </div>
            <div className="text-xs text-text-secondary mt-1">
              {cannibalization?.high_overlap_pairs?.length || 0} high-overlap pairs
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* High Overlap Pairs */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">High Overlap Pairs</h3>
          {cannibalizationQuery.isLoading ? (
            <div className="space-y-2">
              <div className="h-10 bg-surface-600 rounded animate-pulse" />
              <div className="h-10 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : cannibalization?.high_overlap_pairs && cannibalization.high_overlap_pairs.length > 0 ? (
            <div className="space-y-2">
              {cannibalization.high_overlap_pairs.slice(0, 5).map((pair: CannibalizationAnalysis['high_overlap_pairs'][number], index: number) => (
                <div key={index} className="p-3 bg-surface-600 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <div className="text-white text-sm">{pair.product1}</div>
                      <div className="text-text-secondary text-sm">vs {pair.product2}</div>
                    </div>
                    <div className={`text-sm font-medium ${pair.overlap_score > 0.7 ? 'text-danger-400' : 'text-warning-400'}`}>
                      {(pair.overlap_score * 100).toFixed(0)}% overlap
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
              <span className="text-text-tertiary">No high-overlap pairs detected</span>
            </div>
          )}
        </div>

        {/* TURF Analysis */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">TURF Analysis</h3>
          {turfQuery.isLoading ? (
            <div className="space-y-3">
              <div className="h-10 bg-surface-600 rounded animate-pulse" />
              <div className="h-10 bg-surface-600 rounded animate-pulse" />
              <div className="h-10 bg-surface-600 rounded animate-pulse" />
            </div>
          ) : turf ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-surface-600 rounded">
                <span className="text-white">Current Reach</span>
                <span className="text-success-400 font-medium">{(turf.current_reach * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-surface-600 rounded">
                <span className="text-white">Optimal Reach</span>
                <span className="text-primary-400 font-medium">{(turf.optimal_reach * 100).toFixed(0)}%</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-surface-600 rounded">
                <span className="text-white">SKUs for Optimal</span>
                <span className="text-text-secondary font-medium">{turf.optimal_sku_count} / {health?.sku_count || '-'}</span>
              </div>
              <div className="pt-2 text-sm text-text-secondary">
                Potential reach gain: <span className="text-primary-400">+{((turf.optimal_reach - turf.current_reach) * 100).toFixed(0)}%</span>
              </div>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center border border-dashed border-surface-400/30 rounded-lg">
              <span className="text-text-tertiary">No TURF data available</span>
            </div>
          )}
        </div>

        {/* Product Health Table */}
        <div className="card p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Product Health</h3>
          {health?.products && health.products.length > 0 ? (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-400/30">
                      <th className="text-left py-2 text-text-secondary font-medium">Product</th>
                      <th className="text-right py-2 text-text-secondary font-medium">Health Score</th>
                      <th className="text-right py-2 text-text-secondary font-medium">Volume Share</th>
                      <th className="text-right py-2 text-text-secondary font-medium">Margin</th>
                      <th className="text-center py-2 text-text-secondary font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {health.products.slice(0, 10).map((product, index) => {
                      const status = getHealthLabel(product.health_score);
                      const action = product.health_score >= 0.7 ? 'keep' :
                                     product.health_score >= 0.4 ? 'reposition' : 'delist';
                      return (
                        <tr key={product.gtin} className={index < Math.min(health.products!.length, 10) - 1 ? 'border-b border-surface-400/20' : ''}>
                          <td className="py-3">
                            <div className="text-white font-medium">{product.name}</div>
                            <div className="text-xs text-text-tertiary font-mono">{product.gtin}</div>
                          </td>
                          <td className={`py-3 text-right ${status.color}`}>
                            {(product.health_score * 100).toFixed(0)}%
                          </td>
                          <td className="py-3 text-right text-text-secondary">
                            {(product.volume_share * 100).toFixed(1)}%
                          </td>
                          <td className={`py-3 text-right ${product.margin > 0.25 ? 'text-success-400' : product.margin > 0.15 ? 'text-warning-400' : 'text-danger-400'}`}>
                            {(product.margin * 100).toFixed(0)}%
                          </td>
                          <td className="py-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs uppercase ${
                              action === 'keep' ? 'bg-success-500/20 text-success-400' :
                              action === 'reposition' ? 'bg-warning-500/20 text-warning-400' :
                              'bg-danger-500/20 text-danger-400'
                            }`}>
                              {action}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Explainability Card - Portfolio Health Methodology */}
              <ExplainabilityCard
                title="How are recommendations calculated?"
                summary={`Portfolio health is assessed using a multi-factor scoring model that considers volume contribution, margin profile, growth trends, and cannibalization risk. Products scoring above 70% are recommended to keep, 40-70% to reposition, and below 40% to consider delisting.`}
                factors={[
                  { label: 'Volume Share Weight', value: '25%', impact: 'neutral', contribution: 25 },
                  { label: 'Margin Weight', value: '20%', impact: 'neutral', contribution: 20 },
                  { label: 'Growth Trend Weight', value: '20%', impact: 'neutral', contribution: 20 },
                  { label: 'Cannibalization Weight', value: '20%', impact: 'neutral', contribution: 20 },
                  { label: 'Strategic Importance', value: '15%', impact: 'neutral', contribution: 15 },
                ]}
                insights={[
                  `${health.products.filter(p => p.health_score >= 0.7).length} products recommended to keep`,
                  `${health.products.filter(p => p.health_score >= 0.4 && p.health_score < 0.7).length} products recommended to reposition`,
                  `${health.products.filter(p => p.health_score < 0.4).length} products recommended to delist`,
                  `Portfolio HHI of ${health.hhi.toFixed(2)} indicates ${getHHILabel(health.hhi).toLowerCase()}`
                ]}
                nextSteps={[
                  'Review "delist" candidates with sales team for customer dependencies',
                  'Develop repositioning plans for underperforming SKUs',
                  'Consider line extensions for top performers in growing segments'
                ]}
                methodology="Multi-factor weighted scoring with thresholds at 70% (keep), 40% (reposition)"
                variant="default"
              />
            </div>
          ) : (
            <p className="text-text-secondary">No product health data available</p>
          )}
        </div>
      </div>
    </div>
  );
}
